import React, { useEffect, useState, useCallback } from "react";
import { Alert, Badge, Card, Col, Row, Spin, Switch, Typography } from "antd";
import { toast } from "react-toastify";
import SidebarNav from "../sidebar";
import PermissionGate from "../commoncomponent/PermissionGate";
import { getPolicyLinks, updatePolicyLink } from "../../../service/api";

const { Text: TypographyText, Title, Paragraph } = Typography;

const fmtDateTime = (v) => (v ? new Date(v).toLocaleString() : "—");

// Web footer + app "About Paraacrypto" screen already hardcode each policy's
// title/URL — this page only controls whether a given policy link is shown.
const PolicyLinks = () => {
  const [links, setLinks]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState(null);

  const loadLinks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPolicyLinks();
      setLinks(res?.data?.links ?? []);
    } catch {
      toast.error("Failed to load policy links");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadLinks(); }, [loadLinks]);

  const handleToggle = async (policyKey, enabled) => {
    setSavingKey(policyKey);
    try {
      await updatePolicyLink({ policyKey, enabled });
      toast.success("Policy link updated");
      loadLinks();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to update policy link");
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <>
      <SidebarNav />
      <div className="page-wrapper">
        <div className="content container-fluid" style={{ padding: 24 }}>
          <Title level={3} style={{ marginBottom: 4 }}>
            Policy Links
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 24 }}>
            Turn footer/about-page policy links on or off. Titles and URLs are fixed in the app; this only
            controls visibility on web and mobile.
          </Paragraph>

          <Alert
            type="warning"
            showIcon
            message="These toggles affect the web footer and app About page immediately."
            style={{ marginBottom: 24 }}
          />

          <Spin spinning={loading}>
            <Row gutter={[16, 16]}>
              {links.map((l) => (
                <Col key={l.policyKey} xs={24} sm={12} xl={6}>
                  <Card
                    style={{
                      borderColor: l.enabled ? "#b7eb8f" : "#ffa39e",
                      background:  l.enabled ? "#f6ffed" : "#fff1f0",
                    }}
                    styles={{ body: { padding: "16px 20px" } }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <TypographyText strong>{l.label}</TypographyText>
                      <PermissionGate module="policy-link" action="update">
                        <Switch
                          checked={l.enabled}
                          loading={savingKey === l.policyKey}
                          onChange={(checked) => handleToggle(l.policyKey, checked)}
                        />
                      </PermissionGate>
                    </div>

                    <Badge
                      status={l.enabled ? "success" : "error"}
                      text={l.enabled ? "Visible" : "Hidden"}
                      style={{ marginTop: 12, display: "block" }}
                    />

                    {l.updatedAt && (
                      <TypographyText type="secondary" style={{ fontSize: 11, display: "block", marginTop: 8 }}>
                        Last updated: {fmtDateTime(l.updatedAt)}
                        {l.updatedByEmail ? ` by ${l.updatedByEmail}` : ""}
                      </TypographyText>
                    )}
                  </Card>
                </Col>
              ))}
            </Row>
          </Spin>
        </div>
      </div>
    </>
  );
};

export default PolicyLinks;
